// Register before app scripts so app validation cannot swallow the explanation.
// Upload-Lift remains the owner of uploaded files and line-item properties.
(() => {
  const controllers = new WeakMap();
  let nextId = 0;
  const messages = {
    missing: 'Please upload your pet’s photo before adding this item to your cart.',
    uploading: 'Your pet photo is still uploading. Please wait until all uploads complete before adding this item to your cart.',
    failed: 'Your pet photo upload failed. Please retry the upload, or remove the failed file and choose your photo again.',
    removed: 'Your pet photo was removed. Please upload your pet’s photo before adding this item to your cart.',
    unverified: 'Your photo upload has not been confirmed. Please retry the upload and wait for “Upload complete” before adding this item to your cart.',
    unavailable: 'The pet-photo uploader hasn’t loaded. Please refresh this page and try again. If it still doesn’t appear, contact support@laurelandaspen.com before ordering.',
  };
  const detailsFor = form => form?.closest('.product-details[data-photo-required="true"]');
  const requiredNow = details => !details.querySelector('la-holiday-mug input[value="design"]:checked');

  function controller(details) {
    if (controllers.has(details)) return controllers.get(details);
    if (!window.LaPhotoUploadCompletion) return null;
    const abort = new AbortController();
    const state = { details, abort, attempted: false, errors: new Map(), lastFocus: 0 };
    state.uploads = new window.LaPhotoUploadCompletion(details, abort.signal, () => {
      if (state.attempted) feedback(state, false);
    });
    controllers.set(details, state);
    details.addEventListener('change', () => { if (state.attempted) feedback(state, false); }, { signal: abort.signal });
    return state;
  }
  function errorAt(state, anchor, announce) {
    if (!anchor) return null;
    let error = state.errors.get(anchor);
    if (!error?.isConnected) {
      error = document.createElement('p');
      error.id = `la-photo-error-${++nextId}`;
      error.className = 'la-photo-purchase-error';
      error.hidden = true;
      error.tabIndex = -1;
      if (announce) { error.setAttribute('role', 'alert'); error.setAttribute('aria-atomic', 'true'); }
      anchor.after(error);
      state.errors.set(anchor, error);
    }
    return error;
  }
  function feedback(state, focus, form = state.details.querySelector('form[action*="/cart/add"]')) {
    if (!form) return false;
    const status = requiredNow(state.details) ? state.uploads.status(new FormData(form)) : 'completed';
    const message = messages[status] || '';
    const uploader = state.details.querySelector('[data-upload-lift]');
    const uploadAnchor = uploader || state.details.querySelector('[data-personalized-upload-instructions]') || form;
    const uploadError = errorAt(state, uploadAnchor, true);
    const section = state.details.closest('.shopify-section') || state.details;
    const buttons = [...form.querySelectorAll('[type="submit"]'), ...section.querySelectorAll('sticky-add-to-cart button')];
    for (const button of buttons) {
      if (button.type !== 'submit' && !button.matches('sticky-add-to-cart [ref="addToCartButton"]')) continue;
      const anchor = button.closest('add-to-cart-component') || button;
      errorAt(state, anchor, false);
      const tokens = new Set((button.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      tokens.add(uploadError.id);
      button.setAttribute('aria-describedby', [...tokens].join(' '));
    }
    for (const error of state.errors.values()) {
      if (error.textContent !== message) error.textContent = message;
      error.hidden = !message;
    }
    if (uploader) {
      uploader.setAttribute('aria-invalid', message ? 'true' : 'false');
      uploader.setAttribute('aria-describedby', uploadError.id);
    }
    if (message && focus && Date.now() - state.lastFocus > 1500) {
      state.lastFocus = Date.now();
      uploadError.focus({ preventScroll: true });
      const rect = uploadError.getBoundingClientRect();
      const header = document.getElementById('header-group')?.getBoundingClientRect().bottom || 0;
      if (rect.top < Math.max(0, header) || rect.bottom > innerHeight) uploadError.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
    return !message;
  }
  function validateForm(form, focus = true) {
    const details = detailsFor(form);
    if (!details || !requiredNow(details)) return true;
    const state = controller(details);
    if (!state) {
      // Fail closed if the completion tracker itself failed to load.
      let error = details.querySelector('[data-photo-guard-unavailable]');
      if (!error) {
        error = document.createElement('p'); error.dataset.photoGuardUnavailable = '';
        error.className = 'la-photo-purchase-error'; error.setAttribute('role', 'alert');
        error.tabIndex = -1; form.before(error);
      }
      error.textContent = messages.unavailable;
      if (focus) error.focus();
      return false;
    }
    state.attempted = true;
    return feedback(state, focus, form);
  }
  function attempt(event) {
    if (!(event.target instanceof Element)) return;
    let form;
    if (event.type === 'submit') form = event.target;
    else {
      const button = event.target.closest('button, input[type="submit"]');
      if (!button) return;
      if (button.closest('sticky-add-to-cart')) form = button.closest('.shopify-section')?.querySelector('.product-details form[action*="/cart/add"]');
      else if (button.type === 'submit') form = button.form;
    }
    if (!form || validateForm(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
  }
  window.LaPhotoPurchaseGuard = { validateForm };
  window.addEventListener('click', attempt, true);
  window.addEventListener('submit', attempt, true);
  function scan() {
    document.querySelectorAll('.product-details[data-photo-required="true"]').forEach(controller);
  }
  document.addEventListener('DOMContentLoaded', () => {
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }, { once: true });
})();
